import StyledButton from "components/shared/StyledButton";
import { Body, Heading2, SmallBody } from "helpers/TypographyComponents";
import { useTranslation } from "react-i18next";

import styles from "../AvenantTargetStep/style.module.scss";

export type SignatureBehavior = "single" | "multiple";

interface Props {
    value: SignatureBehavior | null;
    onChange: (value: SignatureBehavior) => void;
    onClose: () => void;
    onContinue: () => void;
    onPrevious?: () => void;
}

const SingleIcon = () => (
    <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
    </svg>
);

const MultipleIcon = () => (
    <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <circle cx="9" cy="8" r="3.5" />
        <path d="M2 20c0-3.9 3.1-7 7-7s7 3.1 7 7" />
        <circle cx="17" cy="10" r="2.8" />
        <path d="M22 20c0-3.1-2.2-5.6-5-5.6" />
    </svg>
);

const SignatureBehaviorStep = ({ value, onChange, onClose, onContinue, onPrevious }: Props) => {
    const [t] = useTranslation();

    const tiles: Array<{
        id: SignatureBehavior;
        title: string;
        description: string;
        icon: JSX.Element;
    }> = [
        {
            id: "single",
            title: t("ContractSettings.SignatureBehaviorSingle"),
            description: t("ContractSettings.SignatureBehaviorSingleHint"),
            icon: <SingleIcon />,
        },
        {
            id: "multiple",
            title: t("ContractSettings.SignatureBehaviorMultiple"),
            description: t("ContractSettings.SignatureBehaviorMultipleHint"),
            icon: <MultipleIcon />,
        },
    ];

    return (
        <div className={styles["target-step"]}>
            <div className={styles["wrapper"]}>
                <Heading2>{t("ContractSettings.SignatureBehaviorQuestion")}</Heading2>
                <div className={styles["tiles"]}>
                    {tiles.map((tile) => {
                        const selected = value === tile.id;
                        return (
                            <button
                                key={tile.id}
                                type="button"
                                className={`${styles["tile"]} ${selected ? styles["tile-selected"] : ""}`}
                                onClick={() => onChange(tile.id)}
                            >
                                <span className={styles["tile-icon"]}>{tile.icon}</span>
                                <span className={styles["tile-text"]}>
                                    <Body style={{ margin: 0, fontWeight: 600 }}>{tile.title}</Body>
                                    <SmallBody color="grey" style={{ margin: "4px 0 0" }}>
                                        {tile.description}
                                    </SmallBody>
                                </span>
                                <span className={`${styles["tile-radio"]} ${selected ? styles["tile-radio-on"] : ""}`}>
                                    <span className={styles["tile-radio-inner"]} />
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>
            <div className={styles["actions"]}>
                <div style={{ display: "flex", gap: 8 }}>
                    <StyledButton buttonType="secondary" onClick={onClose}>
                        {t("Cancel")}
                    </StyledButton>
                    {onPrevious && (
                        <StyledButton buttonType="secondary" onClick={onPrevious}>
                            {t("NewElement.Previous")}
                        </StyledButton>
                    )}
                </div>
                <StyledButton buttonType="primary" onClick={onContinue} disabled={!value}>
                    {t("NewElement.Continue")}
                </StyledButton>
            </div>
        </div>
    );
};

export default SignatureBehaviorStep;
